import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCourses } from '../api';
import { useLang } from '../context/LangContext';
import { tCourse, tCategory, tCoursesSubtitle } from '../contentI18n';
import { useSEO } from '../hooks/useSEO';
import SectionIcon from '../components/SectionIcon';
import HeroArt from '../components/HeroArt';
import './Courses.css';

const catColors = {
  English: { bg: '#EFF6FF', color: '#2563EB', border: '#BFDBFE' },
  Math:    { bg: '#F0FDF4', color: '#059669', border: '#A7F3D0' },
  Russian: { bg: '#FEF2F2', color: '#DC2626', border: '#FECACA' },
  Uzbek:   { bg: '#F5F3FF', color: '#7C3AED', border: '#DDD6FE' },
  German:  { bg: '#F9FAFB', color: '#374151', border: '#D1D5DB' },
  Spanish: { bg: '#FFFBEB', color: '#D97706', border: '#FDE68A' },
};

export default function Courses() {
  const { t, lang } = useLang();
  useSEO(t('our_courses'), t('courses_subtitle_plain'));
  const [courses, setCourses] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getCourses().then(d => { setCourses(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  // Category keys stay English because they are what the records are stored
  // with; only the label shown on the tab is translated.
  const categories = ['All', ...new Set(courses.map(c => c.category))];
  const subtitle = tCoursesSubtitle(courses.length, t, lang);
  const filtered = activeCategory === 'All' ? courses : courses.filter(c => c.category === activeCategory);
  const localised = filtered.map(c => tCourse(c, lang));

  return (
    <div className="courses-page">
      <div className="page-hero">
        <div className="page-hero-bg" />
        <HeroArt name="courses" />
        <div className="container">
          <SectionIcon name="courses" />
          <span className="section-tag">{t('what_we_teach')}</span>
          <h1>{t('our_courses')}</h1>
          <p>{subtitle}</p>
        </div>
      </div>

      <div className="container courses-body">
        <div className="category-tabs">
          {categories.map(cat => (
            <button key={cat} className={`cat-tab ${activeCategory === cat ? 'active' : ''}`} onClick={() => setActiveCategory(cat)}>
              {tCategory(cat, lang)}
              <span className="cat-count">{cat === 'All' ? courses.length : courses.filter(c => c.category === cat).length}</span>
            </button>
          ))}
        </div>

        {loading ? <div className="page-loading">{t('loading')}</div> : (
          <div className="all-courses-grid">
            {localised.map(course => {
              const col = catColors[course.category] || catColors.English;
              return (
                <div key={course.id} className="course-full-card" onClick={() => navigate(`/courses/${course.id}`)}>
                  <div className="cfc-header" style={{ background: col.bg, borderBottom: `2px solid ${col.border}` }}>
                    <span className="cfc-icon">{course.icon}</span>
                    <span className="cfc-cat" style={{ color: col.color }}>{course.categoryLabel}</span>
                  </div>
                  <div className="cfc-body">
                    <h3>{course.title}</h3>
                    <p>{course.description}</p>
                    {course.audience && (
                      <div className="cfc-audience"><span className="cfc-audience-label">{t('for_whom')}</span>{course.audience}</div>
                    )}
                    {course.note && <div className="cfc-note">📌 {course.note}</div>}
                    <div className="cfc-formats">
                      {course.formats?.slice(0, 2).map((f, i) => <span key={i} className="cfc-fmt">{f}</span>)}
                    </div>
                    <div className="cfc-meta">
                      <span>⏱ {course.duration}</span>
                      <span>📚 {course.levels}</span>
                    </div>
                    <div className="cfc-cta" style={{ color: col.color }}>{t('view_program')}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
